import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { defineMessages } from 'react-intl';
import { Field } from 'redux-form';
import Isvg from 'react-inlinesvg';

import { parseNumber } from 'helpers/core';
import { PFE_ICONS } from 'constants/icons';
import { schedulePropTypes } from 'constants/propTypes';
import { Card } from 'components/Card';
import { Button, Form } from 'components/Form';
import Message from 'components/Message';
import SvgSchedule from 'components/Svgs/Schedule';
import { NetworkReservationsDropdown, NetworkReservationsStepper } from '../Components';
import './EmptyDetail.scss';

const messages = defineMessages({
  title: {
    id: 'yield-management.empty-detail.title',
    defaultMessage: 'ClassPass members can’t see this class.',
  },
  subtitle: {
    id: 'yield-management.empty-detail.subtitle',
    defaultMessage: 'To make it visible and begin receiving bookings, enable this class.',
  },
  subtitleClassDisabled: {
    id: 'yield-management.empty-detail.subtitle-class-disabled',
    defaultMessage:
      'To make it visible and begin receiving bookings, enable this class. The class will be enabled for this and all future instances.',
  },
  isInThePast: {
    id: 'yield-management.empty-detail.in-the-past',
    defaultMessage: 'This class was disabled.',
  },
  smartspotCopy: {
    id: 'yield-management.empty-detail.smartspot-copy',
    defaultMessage: 'SmartSpot will automatically manage this class’s spots and maximize your revenue.',
  },
  enableBtnPlain: {
    id: 'yield-management.empty-detail.enable-btn-plain',
    defaultMessage: 'Enable',
  },
  enableBtnSpots: {
    id: 'yield-management.empty-detail.enable-btn-spots',
    defaultMessage: 'Enable {currentClasspassSpots, plural, one {# spot} other {# spots}}',
  },
  predictedFill: {
    id: 'yield-management.empty-detail.predicted-fill',
    defaultMessage:
      'Your ClassPass predicted fill for this class is {predictedFill, plural, one {# spot} other {# spots}}.',
  },
  pricingOptionIssue: {
    id: 'yield-management.empty-detail.subtitle-class-disabled-pricing-issue',
    defaultMessage:
      'To make this class visible and begin receiving bookings, update the <a>MINDBODY Pricing Option</a> for this class in your MINDBODY console and then enable this class. The class will be enabled for this and all future instances.',
  },
});

const EmptyDetail = ({
  className,
  classDisabledDueToPricingOptions,
  currentClasspassSpots,
  decrementClasspassSpots,
  incrementClasspassSpots,
  hasScheduleData,
  isFull,
  isInThePast,
  isLoading,
  schedule,
  hasSmartSpotRecommendation,
}) => {
  const { scheduleClass: { classDisabled } = {}, smartspotEnabled, spots } = schedule;
  const { smartspotRecommendedSpots } = spots || {};

  let subtitleMessage;
  if (!classDisabled) {
    subtitleMessage = messages.subtitle;
  } else if (classDisabledDueToPricingOptions) {
    subtitleMessage = messages.pricingOptionIssue;
  } else {
    subtitleMessage = messages.subtitleClassDisabled;
  }

  return (
    <Card className={classNames('empty-detail', className)} centered loading={isLoading}>
      {hasScheduleData && (
        <>
          <SvgSchedule />
          <div className="mt-regular">
            {isInThePast ? (
              <Message message={messages.isInThePast} />
            ) : (
              <>
                <div className="mh-huge">
                  <Message as="p" className="empty-detail__title" message={messages.title} />
                  <Message
                    as="p"
                    message={subtitleMessage}
                    values={{
                      a: text => (
                        <a
                          href="https://support.mindbodyonline.com/s/article/203253823-Pricing-options-memberships"
                          target="_blank"
                          rel="noopener noreferrer">
                          {text}
                        </a>
                      ),
                    }}
                  />
                  {smartspotEnabled && <Message as="p" message={messages.smartspotCopy} />}
                </div>
                <Form className="empty-detail__form">
                  {smartspotEnabled || isFull ? (
                    // if SSenabled or schedule full, do not display pickers, as there is nothing for them to manage and the allocation controls render into a weird state if there are no possible spots
                    <Message as={Button} type={Button.TYPES.SUBMIT} message={messages.enableBtnPlain} />
                  ) : (
                    <>
                      <span className="empty-detail__network-allocation-controls--desktop">
                        <Field
                          {...schedule}
                          name="classpassSpots"
                          parse={parseNumber}
                          component={NetworkReservationsDropdown}
                        />
                      </span>
                      <span className="empty-detail__network-allocation-controls--mobile">
                        <NetworkReservationsStepper
                          currentClasspassSpots={currentClasspassSpots}
                          decrementClasspassSpots={decrementClasspassSpots}
                          incrementClasspassSpots={incrementClasspassSpots}
                          schedule={schedule}
                        />
                      </span>
                      <Message
                        as={Button}
                        type={Button.TYPES.SUBMIT}
                        className="ml-regular"
                        message={messages.enableBtnSpots}
                        values={{ currentClasspassSpots }}
                      />
                    </>
                  )}
                </Form>
                {hasSmartSpotRecommendation && !isFull && (
                  // displaying predicted fill for a full class is pointless even if the prediction is reliable/coherent
                  <div className="empty-detail__predicted-fill-copy mh-huge">
                    <Isvg src={PFE_ICONS.opportunitiesFill} />
                    <Message message={messages.predictedFill} values={{ predictedFill: smartspotRecommendedSpots }} />
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

EmptyDetail.propTypes = {
  className: PropTypes.string,
  currentClasspassSpots: PropTypes.number,
  decrementClasspassSpots: PropTypes.func,
  incrementClasspassSpots: PropTypes.func,
  hasScheduleData: PropTypes.bool,
  isFull: PropTypes.bool,
  isInThePast: PropTypes.bool,
  isLoading: PropTypes.bool,
  classDisabledDueToPricingOptions: PropTypes.bool,
  schedule: PropTypes.shape(schedulePropTypes),
  hasSmartSpotRecommendation: PropTypes.bool,
};

export default EmptyDetail;
